import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../../../components/Layout';
import BopBackLink from '../../../components/BopBackLink';
import ApiInspector from '../../../components/ApiInspector';
import InlineSelect from '../../../components/InlineSelect';
import ComboTabellata from '../../../components/ComboTabellata';
import NominativoClientePicker from '../../../components/NominativoClientePicker';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import { apiFetch } from '../../../lib/api';
import { formatNumber, formatCurrency, formatQuantity } from '../../../utils/formatters';
import { API_DOCS } from './ArticoloSchedaPage.api';

// Tipi
interface UnitaMisura { id: number; simbolo: string; nome: string; }

interface Articolo {
  id: number;
  societaId: number;
  codice: string;
  tipologia: string;
  descrizioneBase: string;
  descrizioneEstesa: string | null;
  umVenditaId: number;
  umAcquistoId: number;
  convertitore: number;
  fornitoreId: number | null;
  prezzoStandard: number | null;
  ean: string | null;
  marcaId: number | null;
  collezioneId: number | null;
  formatoId: number | null;
  qtaMinOrdinabile: number | null;
  qtaMinAcquisto: number | null;
  qtaPerPallet: number | null;
  pzPerConfezione: number | null;
  pzPerMaster: number | null;
  pesoLordoKg: number | null;
  volumeM3: number | null;
  dimensioniProdotto: string | null;
  dimensioniImballo: string | null;
  ecommerce: boolean;
  perPreventivi: boolean;
  gestioneMagazzino: boolean;
  cartellaImmagini: string | null;
  attivo: boolean;
  ultimoImportId: number | null;
  ultimoImport: {
    id: number;
    etichetta: string;
    importatoAt: string;
    stato: string;
    fornitore: { ragioneSociale: string | null; nome: string | null };
  } | null;
  marca: { id: number; nome: string } | null;
  collezione: { id: number; nome: string } | null;
  formato: { id: number; nome: string } | null;
  fornitore: { id: number; ragioneSociale: string | null; nome: string | null } | null;
  societa: { id: number; nome: string };
  umVendita: UnitaMisura;
  umAcquisto: UnitaMisura;
  categoriaMerceologicaId: number | null;
  categoriaConfigurazioneId: number | null;
  effettoId: number | null;
  categoriaMerceologica: { id: number; nome: string } | null;
  categoriaConfigurazione: { id: number; nome: string } | null;
  effetto: { id: number; nome: string } | null;
  // ── Nodo 1: Prodotto + Default ricorrenza ───────────────────────────
  prodottoId: number | null;
  prodotto: { id: number; codice: string; nome: string } | null;
  sottocontoCogeId: number | null;
  sottocontoCoge: { id: number; codice: string; descrizione: string } | null;
  sottotipoRicorrenzaDefault: string | null;
  periodicitaDefault: string | null;
  durataMesiDefault: number | null;
  modalitaPagamentoDefault: string | null;
  tariffaUnitariaDefault: number | string | null;
  tariffaFissaTransazioneDefault: number | string | null;
  percentualeTransazioneDefault: number | string | null;
}

interface SelectItem { id: number; nome: string; }
interface Fornitore { id: number; ragioneSociale: string | null; nome: string | null; }

interface LogRecord {
  id: number;
  articoloId: number;
  utenteId: number | null;
  utenteName: string;
  azione: string;
  dettaglio: string | null;
  createdAt: string;
}

interface CodiceCliente {
  id: number;
  articoloId: number;
  clienteId: number;
  codiceArticoloCliente: string;
  cliente: {
    id: number;
    ragioneSociale: string | null;
    nome: string | null;
    cognome: string | null;
    partitaIva: string | null;
  };
}

interface Riferimenti {
  intercambiabiliComePadre: number;
  intercambiabiliComeFiglio: number;
  modelliConfigurazione: number;
  articoliPreconfigurati: number;
  componentiPreconfigurato: number;
  capitolatiAccessorie: number;
  capitolatiDistinti: number;
  offerteAccessorie: number;
  offerteDistinte: number;
  righePreventivo: number;
  componentiPreventivo: number;
  preventiviDistinti: number;
  totaleRiferimenti: number;
}

type FormState = {
  tipologia: string;
  codice: string;
  descrizioneBase: string;
  descrizioneEstesa: string;
  umVenditaId: string | number;
  umAcquistoId: string | number;
  convertitore: number;
  fornitoreId: string | number;
  prezzoStandard: string | number;
  ean: string;
  marcaId: string | number;
  collezioneId: string | number;
  formatoId: string | number;
  qtaMinOrdinabile: string;
  qtaMinAcquisto: string;
  qtaPerPallet: string;
  pzPerConfezione: string;
  pzPerMaster: string;
  pesoLordoKg: string;
  volumeM3: string;
  dimensioniProdotto: string;
  dimensioniImballo: string;
  ecommerce: boolean;
  perPreventivi: boolean;
  gestioneMagazzino: boolean;
  cartellaImmagini: string;
  categoriaMerceologicaId: number | null;
  categoriaConfigurazioneId: number | null;
  effettoId: number | null;
  // ── Nodo 1: Prodotto + Default ricorrenza ───────────────────────────
  prodottoId: number | null;
  sottocontoCogeId: number | null;
  periodicitaDefault: string;
  durataMesiDefault: string;
  modalitaPagamentoDefault: string;
  tariffaUnitariaDefault: string;
  tariffaFissaTransazioneDefault: string;
  percentualeTransazioneDefault: string;
};

interface ProdottoOption { id: number; codice: string; nome: string; }

const TIPOLOGIE_RICORRENTI = ['LICENZA', 'CANONE', 'CONSUMI', 'COMMISSIONI'] as const;
const isRicorrente = (t: string) => (TIPOLOGIE_RICORRENTI as readonly string[]).includes(t);

// Costanti statiche
const TIPOLOGIE_LABEL: Record<string, string> = {
  PRODOTTO_CONFIGURATO: 'Prodotto configurato',
  ARTICOLO_FISICO: 'Articolo fisico',
  LAVORAZIONE_PRESTAZIONE: 'Lavorazione/Prestazione',
  INTERCAMBIABILE: 'Intercambiabile',
  RAGGRUPPAMENTO_PREZZO_CORPO: 'Raggruppamento (prezzo a corpo)',
  LICENZA: 'Licenza',
  CANONE: 'Canone',
  CONSUMI: 'Consumi',
  COMMISSIONI: 'Commissioni',
};

const PERIODICITA_OPTIONS = [
  { value: 'MENSILE', label: 'Mensile' },
  { value: 'TRIMESTRALE', label: 'Trimestrale' },
  { value: 'SEMESTRALE', label: 'Semestrale' },
  { value: 'ANNUALE', label: 'Annuale' },
];

const MODALITA_PAGAMENTO_OPTIONS = [
  { value: 'A_PREVENTIVO', label: 'A preventivo' },
  { value: 'A_CONSUNTIVO_VOLUMI', label: 'A consuntivo (volumi)' },
  { value: 'A_CONSUNTIVO_TRANSAZIONI', label: 'A consuntivo (transazioni)' },
];

// Etichette delle entità che possono bloccare l'eliminazione di un articolo
const ETICHETTE_USATO_IN: Record<string, string> = {
  intercambiabili:           'Articoli intercambiabili',
  modelliConfigurazione:     'Modelli di configurazione',
  articoliPreconfigurati:    'Articoli preconfigurati',
  componentiPreconfigurato:  'Componenti di preconfigurati',
  capitolatiAccessorie:      'Righe accessorie di capitolati',
  offerteAccessorie:         'Righe accessorie di offerte',
  righePreventivo:           'Righe di preventivi',
  componentiPreventivo:      'Componenti di righe preventivo',
};

export default function ArticoloSchedaPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [articolo, setArticolo] = useState<Articolo | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorCaricamento, setErrorCaricamento] = useState('');

  const [activeTab, setActiveTab] = useState('anagrafica');

  const [form, setForm] = useState<FormState>({
    tipologia: '',
    codice: '',
    descrizioneBase: '',
    descrizioneEstesa: '',
    umVenditaId: '',
    umAcquistoId: '',
    convertitore: 1,
    fornitoreId: '',
    prezzoStandard: '',
    ean: '',
    marcaId: '',
    collezioneId: '',
    formatoId: '',
    qtaMinOrdinabile: '',
    qtaMinAcquisto: '',
    qtaPerPallet: '',
    pzPerConfezione: '',
    pzPerMaster: '',
    pesoLordoKg: '',
    volumeM3: '',
    dimensioniProdotto: '',
    dimensioniImballo: '',
    ecommerce: false,
    perPreventivi: false,
    gestioneMagazzino: false,
    cartellaImmagini: '',
    categoriaMerceologicaId: null,
    categoriaConfigurazioneId: null,
    effettoId: null,
    prodottoId: null,
    sottocontoCogeId: null,
    periodicitaDefault: '',
    durataMesiDefault: '',
    modalitaPagamentoDefault: '',
    tariffaUnitariaDefault: '',
    tariffaFissaTransazioneDefault: '',
    percentualeTransazioneDefault: '',
  });

  // Edit per sezione: id della card attualmente in modifica (null = tutto in lettura)
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [formOriginale, setFormOriginale] = useState<FormState | null>(null);

  const [umList, setUmList] = useState<UnitaMisura[]>([]);
  const [fornitori, setFornitori] = useState<Fornitore[]>([]);
  const [marche, setMarche] = useState<SelectItem[]>([]);
  const [collezioni, setCollezioni] = useState<SelectItem[]>([]);
  const [formati, setFormati] = useState<SelectItem[]>([]);
  const [prodottiOptions, setProdottiOptions] = useState<ProdottoOption[]>([]);
  const [sottocontiCoge, setSottocontiCoge] = useState<{ id: number; codice: string; descrizione: string }[]>([]);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Duplicazione
  const [duplicaOpen, setDuplicaOpen] = useState(false);
  const [duplicaCodice, setDuplicaCodice] = useState('');
  const [duplicaError, setDuplicaError] = useState('');
  const [duplicating, setDuplicating] = useState(false);

  // Disattivazione / riattivazione
  const [togglingAttivo, setTogglingAttivo] = useState(false);
  const [toggleError, setToggleError] = useState('');

  // Eliminazione (hard delete)
  const [eliminaOpen, setEliminaOpen] = useState(false);
  const [eliminating, setEliminating] = useState(false);
  const [eliminaError, setEliminaError] = useState('');
  const [eliminaUsatoIn, setEliminaUsatoIn] = useState<Record<string, number> | null>(null);

  // Storico
  const [logList, setLogList] = useState<LogRecord[]>([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState('');

  // Codici cliente (tab "Codici cliente")
  const [codiciCliente, setCodiciCliente] = useState<CodiceCliente[]>([]);
  const [codiciLoading, setCodiciLoading] = useState(false);
  const [codiciError, setCodiciError] = useState('');
  const [codiciCaricati, setCodiciCaricati] = useState(false);
  const [codiceModalOpen, setCodiceModalOpen] = useState(false);
  const [codiceInModifica, setCodiceInModifica] = useState<CodiceCliente | null>(null);
  const [codiceClienteId, setCodiceClienteId] = useState<number | null>(null);
  const [codiceValore, setCodiceValore] = useState('');
  const [codiceSaving, setCodiceSaving] = useState(false);
  const [codiceModalError, setCodiceModalError] = useState('');

  // Dashboard — riferimenti (dove è usato l'articolo)
  const [riferimenti, setRiferimenti] = useState<Riferimenti | null>(null);
  const [riferimentiLoading, setRiferimentiLoading] = useState(false);
  const [riferimentiError, setRiferimentiError] = useState('');

  const dirty = useMemo(() => {
    if (!formOriginale) return false;
    return JSON.stringify(form) !== JSON.stringify(formOriginale);
  }, [form, formOriginale]);

  const caricaArticolo = async () => {
    if (!id) return;
    setLoading(true);
    setErrorCaricamento('');
    try {
      // @api GET /api/anagrafiche/articoli/:id -> {"success":true,"data":{"id":1,"codice":"...","tipologia":"...","umVendita":{"id":1,"simbolo":"mq"}}}
      const res = await apiFetch<{ success: boolean; data: Articolo }>(`/api/anagrafiche/articoli/${id}`);
      const a = res.data;
      setArticolo(a);
      setForm({
        tipologia: a.tipologia,
        codice: a.codice,
        descrizioneBase: a.descrizioneBase,
        descrizioneEstesa: a.descrizioneEstesa || '',
        umVenditaId: a.umVenditaId,
        umAcquistoId: a.umAcquistoId,
        convertitore: Number(a.convertitore),
        fornitoreId: a.fornitoreId ?? '',
        prezzoStandard: a.prezzoStandard !== null ? Number(a.prezzoStandard) : '',
        ean: a.ean ?? '',
        marcaId: a.marcaId ?? '',
        collezioneId: a.collezioneId ?? '',
        formatoId: a.formatoId ?? '',
        qtaMinOrdinabile: a.qtaMinOrdinabile !== null && a.qtaMinOrdinabile !== undefined
          ? String(a.qtaMinOrdinabile) : '',
        qtaMinAcquisto: a.qtaMinAcquisto !== null && a.qtaMinAcquisto !== undefined
          ? String(a.qtaMinAcquisto) : '',
        qtaPerPallet: a.qtaPerPallet !== null && a.qtaPerPallet !== undefined
          ? String(a.qtaPerPallet) : '',
        pzPerConfezione: a.pzPerConfezione !== null && a.pzPerConfezione !== undefined ? String(a.pzPerConfezione) : '',
        pzPerMaster: a.pzPerMaster !== null && a.pzPerMaster !== undefined ? String(a.pzPerMaster) : '',
        pesoLordoKg: a.pesoLordoKg !== null && a.pesoLordoKg !== undefined ? String(a.pesoLordoKg) : '',
        volumeM3: a.volumeM3 !== null && a.volumeM3 !== undefined ? String(a.volumeM3) : '',
        dimensioniProdotto: a.dimensioniProdotto || '',
        dimensioniImballo: a.dimensioniImballo || '',
        ecommerce: a.ecommerce ?? false,
        perPreventivi: a.perPreventivi ?? false,
        gestioneMagazzino: a.gestioneMagazzino ?? false,
        cartellaImmagini: a.cartellaImmagini || '',
        categoriaMerceologicaId: a.categoriaMerceologicaId ?? null,
        categoriaConfigurazioneId: a.categoriaConfigurazioneId ?? null,
        effettoId: a.effettoId ?? null,
        prodottoId: a.prodottoId ?? null,
        sottocontoCogeId: a.sottocontoCogeId ?? null,
        periodicitaDefault: a.periodicitaDefault ?? '',
        durataMesiDefault: a.durataMesiDefault !== null && a.durataMesiDefault !== undefined ? String(a.durataMesiDefault) : '',
        modalitaPagamentoDefault: a.modalitaPagamentoDefault ?? '',
        tariffaUnitariaDefault: a.tariffaUnitariaDefault !== null && a.tariffaUnitariaDefault !== undefined ? String(a.tariffaUnitariaDefault) : '',
        tariffaFissaTransazioneDefault: a.tariffaFissaTransazioneDefault !== null && a.tariffaFissaTransazioneDefault !== undefined ? String(a.tariffaFissaTransazioneDefault) : '',
        percentualeTransazioneDefault: a.percentualeTransazioneDefault !== null && a.percentualeTransazioneDefault !== undefined ? String(a.percentualeTransazioneDefault) : '',
      });

      const sid = a.societaId;
      const [umRes, marcheRes, formatiRes, fornitoriRes, prodottiRes, sottocontiRes] = await Promise.all([
        // @api GET /api/anagrafiche/articoli/unita-misura -> {"success":true,"data":[{"id":1,"simbolo":"mq","nome":"Metri quadrati"}]}
        apiFetch<{ success: boolean; data: UnitaMisura[] }>(`/api/anagrafiche/articoli/unita-misura?societaId=${sid}`),
        // @api GET /api/anagrafiche/articoli/marche -> {"success":true,"data":[{"id":1,"nome":"..."}]}
        apiFetch<{ success: boolean; data: SelectItem[] }>(`/api/anagrafiche/articoli/marche?societaId=${sid}`),
        // @api GET /api/anagrafiche/articoli/formati -> {"success":true,"data":[{"id":1,"nome":"..."}]}
        apiFetch<{ success: boolean; data: SelectItem[] }>(`/api/anagrafiche/articoli/formati?societaId=${sid}`),
        // @api GET /api/nominativi -> {"data":[{"id":1,"ragioneSociale":"..."}],"total":0}
        apiFetch<{ data: Fornitore[]; total: number }>(`/api/nominativi?ruolo=FORNITORE&limit=200&societaId=${sid}`),
        // @api GET /api/anagrafiche/prodotti -> {"success":true,"data":[{"id":1,"codice":"...","nome":"..."}]}
        apiFetch<{ success: boolean; data: ProdottoOption[] }>(`/api/anagrafiche/prodotti?societaId=${sid}`),
        // @api GET /api/coge/piano-conti/sottoconti-articoli -> {"success":true,"data":[{"id":1,"codice":"04.01.05","descrizione":"..."}]}
        apiFetch<{ success: boolean; data: { id: number; codice: string; descrizione: string }[] }>(`/api/coge/piano-conti/sottoconti-articoli?societaId=${sid}`),
      ]);
      setUmList(umRes.data);
      setMarche(marcheRes.data);
      setFormati(formatiRes.data);
      setFornitori(fornitoriRes.data);
      setProdottiOptions(prodottiRes.data);
      setSottocontiCoge(sottocontiRes.data);

      if (a.marcaId) {
        // @api GET /api/anagrafiche/articoli/collezioni -> {"success":true,"data":[{"id":1,"nome":"..."}]}
        const collRes = await apiFetch<{ success: boolean; data: SelectItem[] }>(
          `/api/anagrafiche/articoli/collezioni?marcaId=${a.marcaId}&societaId=${sid}`
        );
        setCollezioni(collRes.data);
      }
    } catch (err: any) {
      setErrorCaricamento(err.message || 'Errore nel caricamento dell\'articolo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    caricaArticolo();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Azzera campi ricorrenza non più applicabili al cambio di tipologia o modalità
  useEffect(() => {
    const tip = form.tipologia;
    const mod = form.modalitaPagamentoDefault;
    if (!isRicorrente(tip)) {
      // Non ricorrente: azzera tutto tranne prodottoId (può restare valorizzato)
      if (form.periodicitaDefault || form.durataMesiDefault || form.modalitaPagamentoDefault ||
          form.tariffaUnitariaDefault || form.tariffaFissaTransazioneDefault || form.percentualeTransazioneDefault) {
        setForm(prev => ({
          ...prev,
          periodicitaDefault: '',
          durataMesiDefault: '',
          modalitaPagamentoDefault: '',
          tariffaUnitariaDefault: '',
          tariffaFissaTransazioneDefault: '',
          percentualeTransazioneDefault: '',
        }));
      }
      return;
    }
    // Ricorrente: azzera tariffe non applicabili in base alla modalità
    if (mod !== 'A_CONSUNTIVO_VOLUMI' && form.tariffaUnitariaDefault !== '') {
      setForm(prev => ({ ...prev, tariffaUnitariaDefault: '' }));
    }
    if (mod !== 'A_CONSUNTIVO_TRANSAZIONI' &&
        (form.tariffaFissaTransazioneDefault !== '' || form.percentualeTransazioneDefault !== '')) {
      setForm(prev => ({ ...prev, tariffaFissaTransazioneDefault: '', percentualeTransazioneDefault: '' }));
    }
  }, [form.tipologia, form.modalitaPagamentoDefault]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ricarica collezioni quando cambia la marca nel form
  useEffect(() => {
    if (!form.marcaId) {
      setCollezioni([]);
      return;
    }
    const sid = articolo?.societaId;
    apiFetch<{ success: boolean; data: SelectItem[] }>(`/api/anagrafiche/articoli/collezioni?marcaId=${form.marcaId}${sid ? `&societaId=${sid}` : ''}`)
      .then(r => setCollezioni(r.data))
      .catch(() => setCollezioni([]));
  }, [form.marcaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const caricaStorico = async () => {
    if (!id) return;
    setLogLoading(true);
    setLogError('');
    try {
      // @api GET /api/anagrafiche/articoli/:id/log -> {"success":true,"data":[{"id":1,"articoloId":1,"utenteId":1,"utenteName":"...","azione":"Modifica dati","dettaglio":"...","createdAt":"..."}]}
      const res = await apiFetch<{ success: boolean; data: LogRecord[] }>(`/api/anagrafiche/articoli/${id}/log`);
      setLogList(res.data);
    } catch (err: any) {
      setLogError(err.message || 'Errore nel caricamento dello storico');
    } finally {
      setLogLoading(false);
    }
  };

  // Caricamento lazy dello storico al primo accesso al tab
  useEffect(() => {
    if (activeTab === 'storico' && logList.length === 0 && !logError && !logLoading) {
      caricaStorico();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const caricaCodiciCliente = async () => {
    if (!id) return;
    setCodiciLoading(true);
    setCodiciError('');
    try {
      // @api GET /api/anagrafiche/articoli/:id/codici-cliente -> {"success":true,"data":[{"id":1,"articoloId":1,"clienteId":5,"codiceArticoloCliente":"ABC-123","cliente":{"id":5,"ragioneSociale":"...","nome":null,"cognome":null,"partitaIva":"..."}}]}
      const res = await apiFetch<{ success: boolean; data: CodiceCliente[] }>(`/api/anagrafiche/articoli/${id}/codici-cliente`);
      setCodiciCliente(res.data || []);
      setCodiciCaricati(true);
    } catch (err: any) {
      setCodiciError(err.message || 'Errore nel caricamento dei codici cliente');
    } finally {
      setCodiciLoading(false);
    }
  };

  // Caricamento lazy dei codici cliente al primo accesso al tab
  useEffect(() => {
    if (activeTab === 'codici-cliente' && !codiciCaricati && !codiciError && !codiciLoading) {
      caricaCodiciCliente();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const apriCodiceModal = (record: CodiceCliente | null) => {
    setCodiceInModifica(record);
    setCodiceClienteId(record ? record.clienteId : null);
    setCodiceValore(record ? record.codiceArticoloCliente : '');
    setCodiceModalError('');
    setCodiceModalOpen(true);
  };

  const chiudiCodiceModal = () => {
    if (codiceSaving) return;
    setCodiceModalOpen(false);
    setCodiceInModifica(null);
    setCodiceClienteId(null);
    setCodiceValore('');
    setCodiceModalError('');
  };

  const handleSalvaCodice = async () => {
    if (!id) return;
    if (!codiceInModifica && !codiceClienteId) {
      setCodiceModalError('Seleziona il cliente');
      return;
    }
    if (!codiceValore.trim()) {
      setCodiceModalError('Inserisci il codice usato dal cliente');
      return;
    }
    setCodiceSaving(true);
    setCodiceModalError('');
    try {
      if (codiceInModifica) {
        // @api PUT /api/anagrafiche/articoli/:id/codici-cliente/:codiceId -> {"success":true,"data":{"id":1,"articoloId":1,"clienteId":5,"codiceArticoloCliente":"ABC-123","cliente":{"id":5,"ragioneSociale":"...","nome":null,"cognome":null,"partitaIva":"..."}}}
        await apiFetch(`/api/anagrafiche/articoli/${id}/codici-cliente/${codiceInModifica.id}`, {
          method: 'PUT',
          body: JSON.stringify({ codiceArticoloCliente: codiceValore.trim() }),
        });
      } else {
        // @api POST /api/anagrafiche/articoli/:id/codici-cliente -> {"success":true,"data":{"id":1,"articoloId":1,"clienteId":5,"codiceArticoloCliente":"ABC-123","cliente":{"id":5,"ragioneSociale":"...","nome":null,"cognome":null,"partitaIva":"..."}}}
        await apiFetch(`/api/anagrafiche/articoli/${id}/codici-cliente`, {
          method: 'POST',
          body: JSON.stringify({ clienteId: codiceClienteId, codiceArticoloCliente: codiceValore.trim() }),
        });
      }
      await caricaCodiciCliente();
      setCodiceModalOpen(false);
      setCodiceInModifica(null);
      setCodiceClienteId(null);
      setCodiceValore('');
    } catch (err: any) {
      setCodiceModalError(err.message || 'Errore nel salvataggio del codice cliente');
    } finally {
      setCodiceSaving(false);
    }
  };

  const handleEliminaCodice = async (record: CodiceCliente) => {
    if (!id) return;
    const nomeCliente = record.cliente.ragioneSociale
      || `${record.cliente.nome || ''} ${record.cliente.cognome || ''}`.trim()
      || 'cliente';
    if (!window.confirm(`Eliminare il codice "${record.codiceArticoloCliente}" di ${nomeCliente}?`)) return;
    try {
      // @api DELETE /api/anagrafiche/articoli/:id/codici-cliente/:codiceId -> {"success":true}
      await apiFetch(`/api/anagrafiche/articoli/${id}/codici-cliente/${record.id}`, { method: 'DELETE' });
      await caricaCodiciCliente();
    } catch (err: any) {
      setCodiciError(err.message || 'Errore nell\'eliminazione del codice cliente');
    }
  };

  const caricaRiferimenti = async () => {
    if (!id) return;
    setRiferimentiLoading(true);
    setRiferimentiError('');
    try {
      // @api GET /api/anagrafiche/articoli/:id/riferimenti -> {"success":true,"data":{"intercambiabiliComePadre":0,"intercambiabiliComeFiglio":0,"modelliConfigurazione":0,"articoliPreconfigurati":0,"componentiPreconfigurato":0,"capitolatiAccessorie":0,"capitolatiDistinti":0,"offerteAccessorie":0,"offerteDistinte":0,"righePreventivo":0,"componentiPreventivo":0,"preventiviDistinti":0,"totaleRiferimenti":0}}
      const res = await apiFetch<{ success: boolean; data: Riferimenti }>(`/api/anagrafiche/articoli/${id}/riferimenti`);
      setRiferimenti(res.data);
    } catch (err: any) {
      setRiferimentiError(err.message || 'Errore nel caricamento dei riferimenti');
    } finally {
      setRiferimentiLoading(false);
    }
  };

  // Caricamento lazy dei riferimenti al primo accesso al tab Dashboard
  useEffect(() => {
    if (activeTab === 'dashboard' && riferimenti === null && !riferimentiError && !riferimentiLoading) {
      caricaRiferimenti();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleModificaSezione = (sezione: string) => {
    setSaveError('');
    setSaveSuccess(false);
    setFormOriginale({ ...form });
    setEditingSection(sezione);
  };

  const handleAnnullaSezione = () => {
    if (formOriginale) setForm(formOriginale);
    setFormOriginale(null);
    setEditingSection(null);
    setSaveError('');
  };

  const handleCambioTab = (nuovoTab: string) => {
    if (editingSection && dirty) {
      if (!window.confirm('Ci sono modifiche non salvate. Vuoi perderle?')) return;
      if (formOriginale) setForm(formOriginale);
    }
    setFormOriginale(null);
    setEditingSection(null);
    setActiveTab(nuovoTab);
  };

  const handleSalva = async () => {
    if (!id || !articolo) return;

    // Validazioni client (Nodo 1)
    if (isRicorrente(form.tipologia) && !form.prodottoId) {
      setSaveError('Prodotto obbligatorio per tipologie LICENZA / CANONE / CONSUMI / COMMISSIONI');
      return;
    }
    if (form.modalitaPagamentoDefault === 'A_CONSUNTIVO_VOLUMI' &&
        !['CONSUMI', 'COMMISSIONI'].includes(form.tipologia)) {
      setSaveError('Modalità "A consuntivo (volumi)" ammessa solo per tipologie CONSUMI o COMMISSIONI');
      return;
    }
    if (form.modalitaPagamentoDefault === 'A_CONSUNTIVO_TRANSAZIONI' &&
        form.tipologia !== 'COMMISSIONI') {
      setSaveError('Modalità "A consuntivo (transazioni)" ammessa solo per tipologia COMMISSIONI');
      return;
    }

    setSaving(true);
    setSaveError('');
    setSaveSuccess(false);
    const ricorrente = isRicorrente(form.tipologia);
    const mod = form.modalitaPagamentoDefault;
    try {
      // @api PUT /api/anagrafiche/articoli/:id {"tipologia":"...","umVenditaId":1,"umAcquistoId":1,"prodottoId":null,"sottotipoRicorrenzaDefault":null,"periodicitaDefault":null,"durataMesiDefault":null,"modalitaPagamentoDefault":null,"tariffaUnitariaDefault":null,"tariffaFissaTransazioneDefault":null,"percentualeTransazioneDefault":null} -> {"success":true}
      await apiFetch(`/api/anagrafiche/articoli/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          tipologia: form.tipologia,
          descrizioneBase: form.descrizioneBase,
          descrizioneEstesa: form.descrizioneEstesa || null,
          umVenditaId:  form.umVenditaId  !== '' ? Number(form.umVenditaId)  : undefined,
          umAcquistoId: form.umAcquistoId !== '' ? Number(form.umAcquistoId) : undefined,
          convertitore: Number(form.convertitore),
          fornitoreId: form.fornitoreId !== '' ? Number(form.fornitoreId) : null,
          prezzoStandard: form.prezzoStandard !== '' ? Number(form.prezzoStandard) : null,
          ean: form.ean.trim() || null,
          marcaId: form.marcaId !== '' ? Number(form.marcaId) : null,
          collezioneId: form.collezioneId !== '' ? Number(form.collezioneId) : null,
          formatoId: form.formatoId !== '' ? Number(form.formatoId) : null,
          qtaMinOrdinabile: form.qtaMinOrdinabile !== '' ? parseFloat(form.qtaMinOrdinabile) : null,
          qtaMinAcquisto:   form.qtaMinAcquisto   !== '' ? parseFloat(form.qtaMinAcquisto)   : null,
          qtaPerPallet:     form.qtaPerPallet     !== '' ? parseFloat(form.qtaPerPallet)     : null,
          pzPerConfezione:  form.pzPerConfezione  !== '' ? parseFloat(form.pzPerConfezione)  : null,
          pzPerMaster:      form.pzPerMaster      !== '' ? parseFloat(form.pzPerMaster)      : null,
          pesoLordoKg:      form.pesoLordoKg      !== '' ? parseFloat(form.pesoLordoKg)      : null,
          volumeM3:         form.volumeM3         !== '' ? parseFloat(form.volumeM3)         : null,
          dimensioniProdotto: form.dimensioniProdotto.trim() || null,
          dimensioniImballo:  form.dimensioniImballo.trim() || null,
          ecommerce:        form.ecommerce,
          perPreventivi:    form.perPreventivi,
          gestioneMagazzino: form.gestioneMagazzino,
          cartellaImmagini: form.cartellaImmagini?.trim() || null,
          categoriaMerceologicaId:   form.categoriaMerceologicaId,
          categoriaConfigurazioneId: form.categoriaConfigurazioneId,
          effettoId:                 form.effettoId,
          sottocontoCogeId:          form.sottocontoCogeId || null,
          // Nodo 1: Prodotto + Default ricorrenza
          prodottoId: form.prodottoId || null,
          sottotipoRicorrenzaDefault: ricorrente ? form.tipologia : null,
          periodicitaDefault: ricorrente ? (form.periodicitaDefault || null) : null,
          durataMesiDefault: ricorrente && form.durataMesiDefault !== '' ? Number(form.durataMesiDefault) : null,
          modalitaPagamentoDefault: ricorrente ? (mod || null) : null,
          tariffaUnitariaDefault:
            ricorrente && mod === 'A_CONSUNTIVO_VOLUMI' && form.tariffaUnitariaDefault !== ''
              ? Number(form.tariffaUnitariaDefault) : null,
          tariffaFissaTransazioneDefault:
            ricorrente && mod === 'A_CONSUNTIVO_TRANSAZIONI' && form.tariffaFissaTransazioneDefault !== ''
              ? Number(form.tariffaFissaTransazioneDefault) : null,
          percentualeTransazioneDefault:
            ricorrente && mod === 'A_CONSUNTIVO_TRANSAZIONI' && form.percentualeTransazioneDefault !== ''
              ? Number(form.percentualeTransazioneDefault) : null,
        }),
      });
      setSaveSuccess(true);
      setFormOriginale(null);
      setEditingSection(null);
      setLogList([]); // forza ricarico storico al prossimo accesso al tab
      setLogError('');
      await caricaArticolo();
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setSaveError(err.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const apriDuplicaModal = () => {
    if (!articolo) return;
    setDuplicaCodice(articolo.codice);
    setDuplicaError('');
    setDuplicaOpen(true);
  };

  const chiudiDuplicaModal = () => {
    if (duplicating) return;
    setDuplicaOpen(false);
    setDuplicaError('');
  };

  const handleDuplica = async () => {
    if (!articolo) return;
    const codice = duplicaCodice.trim().toUpperCase();
    if (!codice) {
      setDuplicaError('Il codice è obbligatorio');
      return;
    }
    if (codice === articolo.codice.toUpperCase()) {
      setDuplicaError('Il nuovo codice deve essere diverso da quello attuale');
      return;
    }
    setDuplicating(true);
    setDuplicaError('');
    try {
      // @api POST /api/anagrafiche/articoli/:id/duplica {"codice":"..."} -> {"success":true,"data":{"id":1}}
      const res = await apiFetch<{ success: boolean; data: { id: number } }>(
        `/api/anagrafiche/articoli/${articolo.id}/duplica`,
        { method: 'POST', body: JSON.stringify({ codice }) }
      );
      const nuovoId = res.data.id;
      setDuplicaOpen(false);
      setDuplicaCodice('');
      navigate(`/anagrafiche/articoli/${nuovoId}`);
    } catch (err: any) {
      setDuplicaError(err.message || 'Errore nella duplicazione dell\'articolo');
    } finally {
      setDuplicating(false);
    }
  };

  const handleToggleAttivo = async () => {
    if (!articolo || togglingAttivo) return;
    const nuovoStato = !articolo.attivo;
    const conferma = nuovoStato
      ? `Riattivare l'articolo "${articolo.codice}"?`
      : `Disattivare l'articolo "${articolo.codice}"? Non sarà più visibile nelle liste finché non lo riattivi.`;
    if (!window.confirm(conferma)) return;

    setTogglingAttivo(true);
    setToggleError('');
    try {
      // @api PATCH /api/anagrafiche/articoli/:id/attivo {"attivo":true} -> {"success":true,"data":{"attivo":true}}
      await apiFetch(`/api/anagrafiche/articoli/${articolo.id}/attivo`, {
        method: 'PATCH',
        body: JSON.stringify({ attivo: nuovoStato }),
      });
      setLogList([]);
      setLogError('');
      await caricaArticolo();
    } catch (err: any) {
      setToggleError(err.message || 'Errore nell\'aggiornamento dello stato');
    } finally {
      setTogglingAttivo(false);
    }
  };

  const apriEliminaModal = () => {
    setEliminaError('');
    setEliminaUsatoIn(null);
    setEliminaOpen(true);
  };

  const chiudiEliminaModal = () => {
    if (eliminating) return;
    setEliminaOpen(false);
    setEliminaError('');
    setEliminaUsatoIn(null);
  };

  const handleElimina = async () => {
    if (!articolo || eliminating) return;
    setEliminating(true);
    setEliminaError('');
    setEliminaUsatoIn(null);
    try {
      // @api DELETE /api/anagrafiche/articoli/:id -> {"success":true}
      await apiFetch(`/api/anagrafiche/articoli/${articolo.id}`, { method: 'DELETE' });
      navigate('/anagrafiche/articoli');
    } catch (err: any) {
      const dettaglio = err?.payload?.usatoIn || err?.data?.usatoIn || null;
      if (dettaglio && typeof dettaglio === 'object') {
        setEliminaUsatoIn(dettaglio);
        setEliminaError(err.message || 'Articolo utilizzato in altre entità');
      } else {
        setEliminaError(err?.message || 'Errore nell\'eliminazione dell\'articolo');
      }
    } finally {
      setEliminating(false);
    }
  };

  const getIniziali = () => articolo ? articolo.codice.substring(0, 2).toUpperCase() : '??';

  if (loading) {
    return (
      <Layout>
        <div className="articolo-scheda-page">
          <div className="bop-empty-state">Caricamento...</div>
        </div>
      </Layout>
    );
  }

  if (errorCaricamento || !articolo) {
    return (
      <Layout>
        <div className="articolo-scheda-page">
          <BopBackLink to="/anagrafiche/articoli" label="Torna agli articoli" />
          <div className="bop-card">
            <p className="bop-text-danger">{errorCaricamento || 'Articolo non trovato'}</p>
          </div>
        </div>
      </Layout>
    );
  }

  // --- Edit per sezione: una sola card editabile alla volta ---
  const editDatiBase        = editingSection === 'dati-base';
  const editTipologia       = editingSection === 'tipologia';
  const editUnita           = editingSection === 'unita';
  const editLogistica       = editingSection === 'logistica';
  const editPrezzi          = editingSection === 'prezzi';
  const editClassificazione = editingSection === 'classificazione';

  // Header riusabile di ogni card editabile: titolo + Modifica / (Salva · Annulla)
  const renderSezioneHead = (titolo: string, sezione: string) => (
    <div className="articolo-section-head">
      <h3 className="articolo-section-title">{titolo}</h3>
      <div className="articolo-section-head__actions">
        {editingSection === sezione ? (
          <>
            <button
              className="bop-btn bop-btn-secondary bop-btn-sm"
              onClick={handleAnnullaSezione}
              disabled={saving}
            >
              Annulla
            </button>
            <button
              className="bop-btn bop-btn-primary bop-btn-sm"
              onClick={handleSalva}
              disabled={saving || !dirty}
            >
              {saving ? 'Salvataggio…' : 'Salva'}
            </button>
          </>
        ) : (
          <button
            className="bop-btn bop-btn-secondary bop-btn-sm"
            onClick={() => handleModificaSezione(sezione)}
            disabled={editingSection !== null}
            title={editingSection !== null ? 'Salva o annulla la sezione in modifica prima di modificarne un\'altra' : 'Modifica questa sezione'}
          >
            Modifica
          </button>
        )}
      </div>
    </div>
  );

  // --- Unità di misura selezionate (possono essere undefined) ---
  const umVenditaSel  = umList.find(u => u.id === Number(form.umVenditaId));
  const umAcquistoSel = umList.find(u => u.id === Number(form.umAcquistoId));
  const simboloVendita  = umVenditaSel?.simbolo  || '';
  const simboloAcquisto = umAcquistoSel?.simbolo || '';

  // --- Valori numerici usati per calcoli ---
  const convertitoreNum   = Number(form.convertitore);
  const prezzoStandardNum = parseFloat(String(form.prezzoStandard));
  const qtaMinNum    = form.qtaMinOrdinabile !== '' ? parseFloat(form.qtaMinOrdinabile) : null;
  const qtaPalletNum = form.qtaPerPallet     !== '' ? parseFloat(form.qtaPerPallet)     : null;

  // --- Flag di completezza per mostrare il riepilogo ---
  const umCompleta         = !!umVenditaSel && !!umAcquistoSel;
  const convertitoreValido = !isNaN(convertitoreNum) && convertitoreNum > 0;

  // --- Prezzo U.M. di Vendita calcolato (null se non calcolabile) ---
  const prezzoVenditaCalcolato =
    !isNaN(prezzoStandardNum) && convertitoreValido
      ? convertitoreNum * prezzoStandardNum
      : null;

  return (
    <Layout>
      <div className="articolo-scheda-page">
        <BopBackLink to="/anagrafiche/articoli" label="Torna agli articoli" />

        {/* Header */}
        <div className="articolo-header bop-card">
          <div className="articolo-avatar">{getIniziali()}</div>
          <div className="articolo-header-info">
            <h1 className="articolo-nome">{articolo.descrizioneBase}</h1>
            <div className="articolo-badges">
              <span className="bop-badge bop-badge-info">{TIPOLOGIE_LABEL[articolo.tipologia] || articolo.tipologia}</span>
              <span className="bop-badge bop-badge-neutral">{articolo.societa.nome}</span>
              {!articolo.attivo && <span className="bop-badge bop-badge-danger">Disattivato</span>}
            </div>
            <div className="articolo-meta">
              <div className="articolo-meta-item">
                <span className="bop-label">Codice:</span>
                <span className="articolo-codice">{articolo.codice}</span>
              </div>
              <div className="articolo-meta-item">
                <span className="bop-label">Fornitore:</span>
                <span>{articolo.fornitore?.ragioneSociale || articolo.fornitore?.nome || '-'}</span>
              </div>
              <div className="articolo-meta-item">
                <span className="bop-label">Prezzo:</span>
                <span className="articolo-prezzo">
                  {articolo.prezzoStandard !== null ? `€ ${Number(articolo.prezzoStandard).toFixed(2)}` : '-'}
                </span>
              </div>
            </div>
          </div>
          <div className="articolo-header__actions">
            <button
              className="bop-btn bop-btn-secondary"
              onClick={apriDuplicaModal}
              disabled={editingSection !== null}
              title="Crea una copia di questo articolo con un nuovo codice"
            >
              Duplica Articolo
            </button>
            <button
              className="bop-btn bop-btn-secondary"
              onClick={handleToggleAttivo}
              disabled={togglingAttivo || editingSection !== null}
              title={articolo.attivo ? 'Disattiva l\'articolo (sarà escluso dalle liste)' : 'Riattiva l\'articolo'}
            >
              {togglingAttivo
                ? '...'
                : articolo.attivo ? 'Disattiva' : 'Riattiva'}
            </button>
            <button
              className="bop-btn bop-btn-danger"
              onClick={apriEliminaModal}
              disabled={editingSection !== null}
              title="Elimina definitivamente l'articolo (solo se non è mai stato usato)"
            >
              Elimina
            </button>
          </div>
        </div>

        {toggleError && (
          <div className="bop-card">
            <p className="bop-text-danger">{toggleError}</p>
          </div>
        )}

        {saveError && (
          <div className="bop-card">
            <p className="bop-text-danger">{saveError}</p>
          </div>
        )}
        {saveSuccess && (
          <div className="bop-card">
            <p className="bop-text-muted">Modifiche salvate con successo.</p>
          </div>
        )}

        {/* Tabs */}
        <div className="articolo-tabs-container bop-card">
          <div className="articolo-tabs-header">
            <button
              className={`bop-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => handleCambioTab('dashboard')}
            >
              Dashboard
            </button>
            <button
              className={`bop-tab ${activeTab === 'anagrafica' ? 'active' : ''}`}
              onClick={() => handleCambioTab('anagrafica')}
            >
              Anagrafica
            </button>
            <button
              className={`bop-tab ${activeTab === 'unita-prezzi' ? 'active' : ''}`}
              onClick={() => handleCambioTab('unita-prezzi')}
            >
              Unità &amp; Prezzi
            </button>
            <button
              className={`bop-tab ${activeTab === 'logistica' ? 'active' : ''}`}
              onClick={() => handleCambioTab('logistica')}
            >
              Dati logistici
            </button>
            <button
              className={`bop-tab ${activeTab === 'classificazione' ? 'active' : ''}`}
              onClick={() => handleCambioTab('classificazione')}
            >
              Classificazione
            </button>
            <button
              className={`bop-tab ${activeTab === 'codici-cliente' ? 'active' : ''}`}
              onClick={() => handleCambioTab('codici-cliente')}
            >
              Codici cliente
            </button>
            <button
              className={`bop-tab ${activeTab === 'storico' ? 'active' : ''}`}
              onClick={() => handleCambioTab('storico')}
            >
              Storico
            </button>
          </div>

          <div className="bop-tab-content">
            {/* TAB DASHBOARD */}
            {activeTab === 'dashboard' && (
              <div className="articolo-dashboard">
                <div className="bop-card articolo-riferimenti">
                  <h3 className="articolo-section-title">Dove è usato</h3>

                  {riferimentiLoading && (
                    <p className="bop-text-muted">Caricamento riferimenti…</p>
                  )}

                  {riferimentiError && (
                    <p className="bop-text-danger">{riferimentiError}</p>
                  )}

                  {!riferimentiLoading && !riferimentiError && riferimenti && (
                    riferimenti.totaleRiferimenti === 0 ? (
                      <div className="articolo-riferimenti-empty">
                        Questo articolo non è ancora referenziato da nessun'altra entità.
                        Può essere eliminato senza impatti.
                      </div>
                    ) : (
                      <div className="articolo-riferimenti-grid">
                        {/* Configurazioni e relazioni */}
                        <div className="articolo-riferimenti-gruppo">
                          <h4 className="articolo-riferimenti-gruppo-titolo">
                            Configurazioni e relazioni
                          </h4>
                          <ul className="articolo-riferimenti-list">
                            <li className="articolo-riferimenti-item">
                              <span className="articolo-riferimenti-label">
                                Intercambiabili — come padre
                              </span>
                              <span className="articolo-riferimenti-count">
                                {riferimenti.intercambiabiliComePadre}
                              </span>
                            </li>
                            <li className="articolo-riferimenti-item">
                              <span className="articolo-riferimenti-label">
                                Intercambiabili — come figlio
                              </span>
                              <span className="articolo-riferimenti-count">
                                {riferimenti.intercambiabiliComeFiglio}
                              </span>
                            </li>
                            <li className="articolo-riferimenti-item">
                              <span className="articolo-riferimenti-label">
                                Modelli di configurazione (come padre)
                              </span>
                              <span className="articolo-riferimenti-count">
                                {riferimenti.modelliConfigurazione}
                              </span>
                            </li>
                            <li className="articolo-riferimenti-item">
                              <span className="articolo-riferimenti-label">
                                Articoli preconfigurati (come padre)
                              </span>
                              <span className="articolo-riferimenti-count">
                                {riferimenti.articoliPreconfigurati}
                              </span>
                            </li>
                            <li className="articolo-riferimenti-item">
                              <span className="articolo-riferimenti-label">
                                Componente di articoli preconfigurati
                              </span>
                              <span className="articolo-riferimenti-count">
                                {riferimenti.componentiPreconfigurato}
                              </span>
                            </li>
                          </ul>
                        </div>

                        {/* Documenti commerciali */}
                        <div className="articolo-riferimenti-gruppo">
                          <h4 className="articolo-riferimenti-gruppo-titolo">
                            Documenti commerciali
                          </h4>
                          <ul className="articolo-riferimenti-list">
                            <li className="articolo-riferimenti-item">
                              <span className="articolo-riferimenti-label">
                                Righe di preventivi
                                {riferimenti.righePreventivo > 0 && (
                                  <span className="articolo-riferimenti-sublabel">
                                    in {riferimenti.preventiviDistinti} preventiv{riferimenti.preventiviDistinti === 1 ? 'o' : 'i'}
                                  </span>
                                )}
                              </span>
                              <span className="articolo-riferimenti-count">
                                {riferimenti.righePreventivo}
                              </span>
                            </li>
                            <li className="articolo-riferimenti-item">
                              <span className="articolo-riferimenti-label">
                                Componenti di righe preventivo
                              </span>
                              <span className="articolo-riferimenti-count">
                                {riferimenti.componentiPreventivo}
                              </span>
                            </li>
                            <li className="articolo-riferimenti-item">
                              <span className="articolo-riferimenti-label">
                                Righe accessorie di capitolati
                                {riferimenti.capitolatiAccessorie > 0 && (
                                  <span className="articolo-riferimenti-sublabel">
                                    in {riferimenti.capitolatiDistinti} capitolat{riferimenti.capitolatiDistinti === 1 ? 'o' : 'i'}
                                  </span>
                                )}
                              </span>
                              <span className="articolo-riferimenti-count">
                                {riferimenti.capitolatiAccessorie}
                              </span>
                            </li>
                            <li className="articolo-riferimenti-item">
                              <span className="articolo-riferimenti-label">
                                Righe accessorie di offerte
                                {riferimenti.offerteAccessorie > 0 && (
                                  <span className="articolo-riferimenti-sublabel">
                                    in {riferimenti.offerteDistinte} offert{riferimenti.offerteDistinte === 1 ? 'a' : 'e'}
                                  </span>
                                )}
                              </span>
                              <span className="articolo-riferimenti-count">
                                {riferimenti.offerteAccessorie}
                              </span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    )
                  )}
                </div>

                <div className="bop-card articolo-stats-placeholder">
                  <h3 className="articolo-section-title">Statistiche e KPI</h3>
                  <div className="articolo-placeholder-content">
                    <div className="articolo-placeholder-icon">📊</div>
                    <p className="articolo-placeholder-text">
                      Statistiche vendite, marginalità e rotazione — disponibili prossimamente
                    </p>
                  </div>
                </div>

                <ApiInspector docs={API_DOCS} />
              </div>
            )}

            {/* TAB ANAGRAFICA */}
            {activeTab === 'anagrafica' && (
              <div className="articolo-dettagli">
                <div className="bop-card">
                  {renderSezioneHead('Dati base', 'dati-base')}

                  <div className="bop-form-group">
                    <label className="bop-label">Codice *</label>
                    <input
                      type="text"
                      className="bop-input"
                      value={form.codice}
                      readOnly
                      disabled
                      title="Il codice articolo non è modificabile dopo la creazione"
                    />
                  </div>

                  <div className="bop-form-group">
                    <label className="bop-label" htmlFor="articolo-ean">Codice EAN</label>
                    <input
                      id="articolo-ean"
                      type="text"
                      className="bop-input"
                      value={form.ean}
                      onChange={(e) => setForm({ ...form, ean: e.target.value })}
                      readOnly={!editDatiBase}
                      placeholder="es. 8053017891811"
                    />
                    <p className="bop-text-muted">
                      Codice a barre. Usato dall'import ordini delle catene come chiave di
                      aggancio: sopravvive ai cambi di codifica, nostra e del cliente.
                    </p>
                  </div>

                  <div className="bop-form-group">
                    <label className="bop-label">Descrizione Base *</label>
                    <input
                      type="text"
                      className="bop-input"
                      value={form.descrizioneBase}
                      onChange={(e) => setForm({ ...form, descrizioneBase: e.target.value })}
                      readOnly={!editDatiBase}
                    />
                  </div>

                  <div className="bop-form-group">
                    <label className="bop-label">Descrizione Estesa</label>
                    <textarea
                      className="bop-input"
                      value={form.descrizioneEstesa}
                      onChange={(e) => setForm({ ...form, descrizioneEstesa: e.target.value })}
                      rows={4}
                      readOnly={!editDatiBase}
                    />
                  </div>

                  <div className="bop-form-row">
                    <div className="bop-form-group">
                      <label className="bop-label">E-commerce</label>
                      <div className="articolo-checkbox-group">
                        <input
                          type="checkbox"
                          id="ecommerce"
                          checked={form.ecommerce}
                          onChange={(e) => setForm({ ...form, ecommerce: e.target.checked })}
                          disabled={!editDatiBase}
                        />
                        <label htmlFor="ecommerce">Articolo visibile sull'e-commerce</label>
                      </div>
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">Preventivi</label>
                      <div className="articolo-checkbox-group">
                        <input
                          type="checkbox"
                          id="perPreventivi"
                          checked={form.perPreventivi}
                          onChange={(e) => setForm({ ...form, perPreventivi: e.target.checked })}
                          disabled={!editDatiBase}
                        />
                        <label htmlFor="perPreventivi">Articolo per preventivi</label>
                      </div>
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">Gestione magazzino</label>
                      <div className="articolo-checkbox-group">
                        <input
                          type="checkbox"
                          id="gestioneMagazzino"
                          checked={form.gestioneMagazzino}
                          onChange={(e) => setForm({ ...form, gestioneMagazzino: e.target.checked })}
                          disabled={!editDatiBase}
                        />
                        <label htmlFor="gestioneMagazzino">Articolo movimentabile a magazzino</label>
                      </div>
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">Cartella Immagini</label>
                      <input
                        type="text"
                        className="bop-input"
                        value={form.cartellaImmagini}
                        onChange={(e) => setForm({ ...form, cartellaImmagini: e.target.value })}
                        placeholder="Es. /immagini/piastrelle/serie-urban"
                        readOnly={!editDatiBase}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB UNITÀ & PREZZI */}
            {activeTab === 'unita-prezzi' && (
              <div className="articolo-dettagli">

                <div className="bop-card">
                  {renderSezioneHead('Unità di misura', 'unita')}

                  {/* Riepilogo parlante — sempre visibile */}
                  {umCompleta && convertitoreValido ? (
                    <div className="bop-articolo__um-riepilogo">
                      <p className="bop-articolo__um-riepilogo-title">Come funziona:</p>
                      <p className="bop-articolo__um-riepilogo-line">
                        Questo articolo si vende in <strong>{umVenditaSel!.nome.toLowerCase()}</strong>
                        {' '}e si acquista in <strong>{umAcquistoSel!.nome.toLowerCase()}</strong>.
                      </p>
                      <p className="bop-articolo__um-riepilogo-line">
                        1 {simboloVendita} contiene {formatNumber(convertitoreNum)} {simboloAcquisto}.
                      </p>
                      {qtaMinNum !== null && !isNaN(qtaMinNum) && (
                        <p className="bop-articolo__um-riepilogo-line">
                          Quantità minima ordinabile: {formatQuantity(qtaMinNum)} {simboloVendita}.
                        </p>
                      )}
                      {qtaPalletNum !== null && !isNaN(qtaPalletNum) && (
                        <p className="bop-articolo__um-riepilogo-line">
                          {formatQuantity(qtaPalletNum)} {simboloVendita} per pallet.
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="bop-articolo__um-riepilogo bop-articolo__um-riepilogo--empty">
                      Completa i campi sottostanti per vedere il riepilogo delle unità di misura.
                    </div>
                  )}

                  <div className="bop-form-row">
                    <div className="bop-form-group">
                      <label className="bop-label">U.M. Vendita *</label>
                      <InlineSelect
                        value={form.umVenditaId}
                        onChange={(id) => setForm(prev => ({ ...prev, umVenditaId: id }))}
                        opzioni={umList.map(u => ({ id: u.id, label: `${u.simbolo} — ${u.nome}` }))}
                        placeholder="Seleziona U.M. vendita..."
                        disabled={!editUnita}
                        onCrea={async (nome) => {
                          // @api POST /api/anagrafiche/articoli/unita-misura {"societaId":1,"simbolo":"...","nome":"..."} -> {"success":true,"data":{"id":1,"simbolo":"...","nome":"..."}}
                          const res = await apiFetch<{ success: boolean; data: UnitaMisura }>(
                            '/api/anagrafiche/articoli/unita-misura',
                            { method: 'POST', body: JSON.stringify({ societaId: articolo.societaId, simbolo: nome, nome }) }
                          );
                          const nuova = res.data;
                          setUmList(prev => [...prev, nuova]);
                          return { id: nuova.id, label: `${nuova.simbolo} — ${nuova.nome}` };
                        }}
                      />
                      <p className="bop-field__hint">L'unità in cui l'articolo viene venduto.</p>
                    </div>

                    <div className="bop-form-group">
                      <label className="bop-label">U.M. Acquisto *</label>
                      <InlineSelect
                        value={form.umAcquistoId}
                        onChange={(id) => setForm(prev => ({ ...prev, umAcquistoId: id }))}
                        opzioni={umList.map(u => ({ id: u.id, label: `${u.simbolo} — ${u.nome}` }))}
                        placeholder="Seleziona U.M. acquisto..."
                        disabled={!editUnita}
                        onCrea={async (nome) => {
                          const res = await apiFetch<{ success: boolean; data: UnitaMisura }>(
                            '/api/anagrafiche/articoli/unita-misura',
                            { method: 'POST', body: JSON.stringify({ societaId: articolo.societaId, simbolo: nome, nome }) }
                          );
                          const nuova = res.data;
                          setUmList(prev => [...prev, nuova]);
                          return { id: nuova.id, label: `${nuova.simbolo} — ${nuova.nome}` };
                        }}
                      />
                      <p className="bop-field__hint">L'unità in cui l'articolo viene acquistato dal fornitore.</p>
                    </div>

                    <div className="bop-form-group">
                      <label className="bop-label">
                        Convertitore (1 {simboloVendita || 'vnd'} = X {simboloAcquisto || 'acq'})
                      </label>
                      <input
                        type="number"
                        className="bop-input"
                        value={form.convertitore}
                        onChange={(e) => setForm({ ...form, convertitore: Number(e.target.value) })}
                        min="0.01"
                        step="0.01"
                        disabled={!editUnita}
                      />
                      <p className="bop-field__hint">
                        {umCompleta && convertitoreValido
                          ? `1 ${simboloVendita} contiene ${formatNumber(convertitoreNum)} ${simboloAcquisto}.`
                          : 'Quante unità di acquisto compongono una unità di vendita.'}
                      </p>
                    </div>

                    <div className="bop-form-group">
                      <label className="bop-label">
                        Qta min vendita {simboloVendita ? `(${simboloVendita})` : ''}
                      </label>
                      <input
                        type="number"
                        className="bop-input"
                        value={form.qtaMinOrdinabile}
                        onChange={(e) => setForm({ ...form, qtaMinOrdinabile: e.target.value })}
                        step="0.001"
                        min="0"
                        disabled={!editUnita}
                      />
                      <p className="bop-field__hint">
                        {qtaMinNum !== null && !isNaN(qtaMinNum) && simboloVendita
                          ? `Quantità minima: ${formatQuantity(qtaMinNum)} ${simboloVendita}.`
                          : 'Espressa in U.M. di vendita.'}
                      </p>
                    </div>

                    <div className="bop-form-group">
                      <label className="bop-label">
                        Qta min acquisto {simboloAcquisto ? `(${simboloAcquisto})` : ''}
                      </label>
                      <input
                        type="number"
                        className="bop-input"
                        value={form.qtaMinAcquisto}
                        onChange={(e) => setForm({ ...form, qtaMinAcquisto: e.target.value })}
                        step="0.001"
                        min="0"
                        disabled={!editUnita}
                      />
                      <p className="bop-field__hint">
                        {form.qtaMinAcquisto !== '' && !isNaN(parseFloat(form.qtaMinAcquisto)) && simboloAcquisto
                          ? `Quantità minima: ${formatQuantity(parseFloat(form.qtaMinAcquisto))} ${simboloAcquisto}.`
                          : 'Espressa in U.M. di acquisto.'}
                      </p>
                    </div>

                    <div className="bop-form-group">
                      <label className="bop-label">
                        Qtà per pallet {simboloVendita ? `(${simboloVendita})` : ''}
                      </label>
                      <input
                        type="number"
                        className="bop-input"
                        value={form.qtaPerPallet}
                        onChange={(e) => setForm({ ...form, qtaPerPallet: e.target.value })}
                        step="0.001"
                        min="0"
                        disabled={!editUnita}
                      />
                      <p className="bop-field__hint">
                        {qtaPalletNum !== null && !isNaN(qtaPalletNum) && simboloVendita
                          ? `${formatQuantity(qtaPalletNum)} ${simboloVendita} per pallet.`
                          : 'Espressa in U.M. di vendita.'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bop-card">
                  {renderSezioneHead('Prezzi e fornitore', 'prezzi')}

                  <div className="bop-form-row">
                    <div className="bop-form-group">
                      <label className="bop-label">Fornitore</label>
                      <select
                        className="bop-select"
                        value={form.fornitoreId}
                        onChange={(e) => setForm({ ...form, fornitoreId: e.target.value })}
                        disabled={!editPrezzi}
                      >
                        <option value="">Nessun fornitore</option>
                        {fornitori.map(f => (
                          <option key={f.id} value={f.id}>{f.ragioneSociale || f.nome}</option>
                        ))}
                      </select>
                    </div>

                    <div className="bop-form-group">
                      <label className="bop-label">
                        Prezzo U.M. di Acquisto {simboloAcquisto ? `(€/${simboloAcquisto})` : '(€)'}
                      </label>
                      <input
                        type="number"
                        className="bop-input"
                        value={form.prezzoStandard}
                        onChange={(e) => setForm({ ...form, prezzoStandard: e.target.value })}
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        disabled={!editPrezzi}
                      />
                      <p className="bop-field__hint">
                        Prezzo di listino per una unità di acquisto{simboloAcquisto ? ` (${simboloAcquisto})` : ''}.{' '}
                        <Link
                          to={`/anagrafiche/articoli/${articolo.id}/storico-prezzi`}
                          className="articolo-prezzi__link"
                        >
                          Storico prezzi di listino
                        </Link>
                      </p>
                    </div>

                    {/* Prezzo U.M. di Vendita calcolato (read-only) */}
                    <div className="bop-form-group">
                      <label className="bop-label">Prezzo U.M. di Vendita</label>
                      <div
                        className={`bop-articolo__prezzo-calcolato${
                          prezzoVenditaCalcolato === null ? ' bop-articolo__prezzo-calcolato--empty' : ''
                        }`}
                      >
                        <p className="bop-articolo__prezzo-calcolato-value">
                          {prezzoVenditaCalcolato !== null
                            ? `${formatCurrency(prezzoVenditaCalcolato)} €${simboloVendita ? `/${simboloVendita}` : ''}`
                            : '—'}
                        </p>
                        {prezzoVenditaCalcolato !== null && (
                          <p className="bop-articolo__prezzo-calcolato-formula">
                            {formatNumber(convertitoreNum)} {simboloAcquisto} × {formatCurrency(prezzoStandardNum)} €/{simboloAcquisto}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <ApiInspector docs={API_DOCS} />
              </div>
            )}

            {/* TAB DATI LOGISTICI */}
            {activeTab === 'logistica' && (
              <div className="articolo-dettagli">
                <div className="bop-card">
                  {renderSezioneHead('Dati logistici', 'logistica')}

                  <div className="bop-form-row">
                    <div className="bop-form-group">
                      <label className="bop-label">Pz per confezione</label>
                      <input
                        type="number"
                        className="bop-input"
                        value={form.pzPerConfezione}
                        onChange={(e) => setForm({ ...form, pzPerConfezione: e.target.value })}
                        step="1"
                        min="0"
                        disabled={!editLogistica}
                      />
                      <p className="bop-field__hint">Pezzi nella confezione di vendita.</p>
                    </div>

                    <div className="bop-form-group">
                      <label className="bop-label">Pz per master</label>
                      <input
                        type="number"
                        className="bop-input"
                        value={form.pzPerMaster}
                        onChange={(e) => setForm({ ...form, pzPerMaster: e.target.value })}
                        step="1"
                        min="0"
                        disabled={!editLogistica}
                      />
                      <p className="bop-field__hint">Confezioni nel cartone master.</p>
                    </div>

                    <div className="bop-form-group">
                      <label className="bop-label">Peso lordo (kg)</label>
                      <input
                        type="number"
                        className="bop-input"
                        value={form.pesoLordoKg}
                        onChange={(e) => setForm({ ...form, pesoLordoKg: e.target.value })}
                        step="0.001"
                        min="0"
                        disabled={!editLogistica}
                      />
                    </div>

                    <div className="bop-form-group">
                      <label className="bop-label">Volume (m³)</label>
                      <input
                        type="number"
                        className="bop-input"
                        value={form.volumeM3}
                        onChange={(e) => setForm({ ...form, volumeM3: e.target.value })}
                        step="0.000001"
                        min="0"
                        disabled={!editLogistica}
                      />
                    </div>
                  </div>

                  <div className="bop-form-row">
                    <div className="bop-form-group">
                      <label className="bop-label">Dimensioni prodotto (cm)</label>
                      <input
                        type="text"
                        className="bop-input"
                        value={form.dimensioniProdotto}
                        onChange={(e) => setForm({ ...form, dimensioniProdotto: e.target.value })}
                        placeholder="es. 13,4 x 1,8 x 20,4"
                        disabled={!editLogistica}
                      />
                      <p className="bop-field__hint">Larghezza x altezza x profondità.</p>
                    </div>

                    <div className="bop-form-group">
                      <label className="bop-label">Dimensioni imballo (cm)</label>
                      <input
                        type="text"
                        className="bop-input"
                        value={form.dimensioniImballo}
                        onChange={(e) => setForm({ ...form, dimensioniImballo: e.target.value })}
                        placeholder="es. 26 x 38 x 47"
                        disabled={!editLogistica}
                      />
                    </div>
                  </div>
                </div>

                <ApiInspector docs={API_DOCS} />
              </div>
            )}

            {/* TAB CLASSIFICAZIONE */}
            {activeTab === 'classificazione' && (
              <div className="articolo-dettagli">
                <div className="bop-card">
                  {renderSezioneHead('Classificazione', 'classificazione')}

                  <div className="bop-form-row">
                    <div className="bop-form-group">
                      <label className="bop-label">Marca</label>
                      <InlineSelect
                        value={form.marcaId}
                        onChange={(id) => setForm(prev => ({ ...prev, marcaId: id, collezioneId: '' }))}
                        opzioni={marche.map(m => ({ id: m.id, label: m.nome }))}
                        placeholder="Nessuna marca"
                        disabled={!editClassificazione}
                        onCrea={async (nome) => {
                          // @api POST /api/anagrafiche/articoli/marche/find-or-create {"societaId":1,"nome":"..."} -> {"success":true,"data":{"id":1,"nome":"..."}}
                          const res = await apiFetch<{ success: boolean; data: SelectItem }>(
                            '/api/anagrafiche/articoli/marche/find-or-create',
                            { method: 'POST', body: JSON.stringify({ societaId: articolo.societaId, nome }) }
                          );
                          const nuova = res.data;
                          setMarche(prev => [...prev, nuova]);
                          return { id: nuova.id, label: nuova.nome };
                        }}
                      />
                    </div>

                    <div className="bop-form-group">
                      <label className="bop-label">Collezione</label>
                      <InlineSelect
                        value={form.collezioneId}
                        onChange={(id) => setForm(prev => ({ ...prev, collezioneId: id }))}
                        opzioni={collezioni.map(c => ({ id: c.id, label: c.nome }))}
                        placeholder="Nessuna collezione"
                        disabled={!editClassificazione || !form.marcaId}
                        onCrea={async (nome) => {
                          // @api POST /api/anagrafiche/articoli/collezioni/find-or-create {"societaId":1,"marcaId":1,"nome":"..."} -> {"success":true,"data":{"id":1,"nome":"..."}}
                          const res = await apiFetch<{ success: boolean; data: SelectItem }>(
                            '/api/anagrafiche/articoli/collezioni/find-or-create',
                            { method: 'POST', body: JSON.stringify({ societaId: articolo.societaId, marcaId: Number(form.marcaId), nome }) }
                          );
                          const nuova = res.data;
                          setCollezioni(prev => [...prev, nuova]);
                          return { id: nuova.id, label: nuova.nome };
                        }}
                      />
                    </div>

                    <div className="bop-form-group">
                      <label className="bop-label">Formato</label>
                      <InlineSelect
                        value={form.formatoId}
                        onChange={(id) => setForm(prev => ({ ...prev, formatoId: id }))}
                        opzioni={formati.map(f => ({ id: f.id, label: f.nome }))}
                        placeholder="Nessun formato"
                        disabled={!editClassificazione}
                        onCrea={async (nome) => {
                          // @api POST /api/anagrafiche/articoli/formati/find-or-create {"societaId":1,"nome":"..."} -> {"success":true,"data":{"id":1,"nome":"..."}}
                          const res = await apiFetch<{ success: boolean; data: SelectItem }>(
                            '/api/anagrafiche/articoli/formati/find-or-create',
                            { method: 'POST', body: JSON.stringify({ societaId: articolo.societaId, nome }) }
                          );
                          const nuova = res.data;
                          setFormati(prev => [...prev, nuova]);
                          return { id: nuova.id, label: nuova.nome };
                        }}
                      />
                    </div>

                    <div className="bop-form-group">
                      <label className="bop-label">Categ. Merceologica</label>
                      {/* @api GET /api/categorie-merceologiche -> {"success":true,"data":[{"id":1,"nome":"..."}]} */}
                      {/* @api POST /api/categorie-merceologiche {"nome":"...","societaId":1} -> {"success":true,"data":{"id":1,"nome":"..."}} */}
                      <ComboTabellata
                        label="Categ. Merceologica"
                        value={form.categoriaMerceologicaId}
                        onChange={(id) => setForm(prev => ({ ...prev, categoriaMerceologicaId: id }))}
                        societaId={articolo.societaId}
                        endpoint="/api/categorie-merceologiche"
                        disabled={!editClassificazione}
                      />
                    </div>

                    <div className="bop-form-group">
                      <label className="bop-label">Categoria per configurazione</label>
                      {/* @api GET /api/categorie-configurazione -> {"success":true,"data":[{"id":1,"nome":"..."}]} */}
                      {/* @api POST /api/categorie-configurazione {"nome":"...","societaId":1} -> {"success":true,"data":{"id":1,"nome":"..."}} */}
                      <ComboTabellata
                        label="Categ. Configurazione"
                        value={form.categoriaConfigurazioneId}
                        onChange={(id) => setForm(prev => ({ ...prev, categoriaConfigurazioneId: id }))}
                        societaId={articolo.societaId}
                        endpoint="/api/categorie-configurazione"
                        disabled={!editClassificazione}
                      />
                    </div>

                    <div className="bop-form-group">
                      <label className="bop-label">Effetto</label>
                      {/* @api GET /api/effetti -> {"success":true,"data":[{"id":1,"nome":"..."}]} */}
                      {/* @api POST /api/effetti {"nome":"...","societaId":1} -> {"success":true,"data":{"id":1,"nome":"..."}} */}
                      <ComboTabellata
                        label="Effetto"
                        value={form.effettoId}
                        onChange={(id) => setForm(prev => ({ ...prev, effettoId: id }))}
                        societaId={articolo.societaId}
                        endpoint="/api/effetti"
                        disabled={!editClassificazione}
                      />
                    </div>

                    <div className="bop-form-group">
                      <label className="bop-label">Sottoconto</label>
                      <select
                        className="bop-select"
                        value={form.sottocontoCogeId ?? ''}
                        onChange={(e) => setForm(prev => ({ ...prev, sottocontoCogeId: e.target.value === '' ? null : Number(e.target.value) }))}
                        disabled={!editClassificazione}
                      >
                        <option value="">— Nessuno —</option>
                        {/* Sottoconto già associato ma non più tra i flaggati attivi: mostralo comunque per non perderlo */}
                        {articolo.sottocontoCoge && !sottocontiCoge.some(s => s.id === articolo.sottocontoCoge!.id) && (
                          <option value={articolo.sottocontoCoge.id}>
                            {articolo.sottocontoCoge.codice} — {articolo.sottocontoCoge.descrizione} (non più disponibile)
                          </option>
                        )}
                        {sottocontiCoge.map(s => (
                          <option key={s.id} value={s.id}>{s.codice} — {s.descrizione}</option>
                        ))}
                      </select>
                      <p className="bop-field__hint">Sottoconti del piano CO.GE marcati "Mostra in anagrafica articoli".</p>
                    </div>
                  </div>
                </div>

                <ApiInspector docs={API_DOCS} />
              </div>
            )}

            {/* TAB ANAGRAFICA — Tipologia & ricorrenza */}
            {activeTab === 'anagrafica' && (
              <div className="articolo-dettagli">
                <div className="bop-card">
                  {renderSezioneHead('Tipologia e ricorrenza', 'tipologia')}

                  <div className="bop-form-row">
                    <div className="bop-form-group">
                      <label className="bop-label">Tipologia *</label>
                      <select
                        className="bop-select"
                        value={form.tipologia}
                        onChange={(e) => setForm({ ...form, tipologia: e.target.value })}
                        disabled={!editTipologia}
                      >
                        <option value="PRODOTTO_CONFIGURATO">Prodotto configurato</option>
                        <option value="ARTICOLO_FISICO">Articolo fisico</option>
                        <option value="LAVORAZIONE_PRESTAZIONE">Lavorazione/Prestazione</option>
                        <option value="INTERCAMBIABILE">Intercambiabile</option>
                        <option value="RAGGRUPPAMENTO_PREZZO_CORPO">Raggruppamento (prezzo a corpo)</option>
                        <option value="LICENZA">Licenza</option>
                        <option value="CANONE">Canone</option>
                        <option value="CONSUMI">Consumi</option>
                        <option value="COMMISSIONI">Commissioni</option>
                      </select>
                      <p className="bop-field__hint">
                        Le tipologie ricorrenti (Licenza / Canone / Consumi / Commissioni) richiedono un prodotto e abilitano i default di ricorrenza.
                      </p>
                    </div>

                    <div className="bop-form-group">
                      <label className="bop-label">
                        Prodotto {isRicorrente(form.tipologia) && <span className="bop-text-danger">*</span>}
                      </label>
                      <select
                        className="bop-select"
                        value={form.prodottoId ?? ''}
                        onChange={(e) => setForm({ ...form, prodottoId: e.target.value === '' ? null : Number(e.target.value) })}
                        disabled={!editTipologia}
                      >
                        <option value="">— Nessuno —</option>
                        {prodottiOptions.map(p => (
                          <option key={p.id} value={p.id}>{p.codice} — {p.nome}</option>
                        ))}
                      </select>
                      <p className="bop-field__hint">
                        {isRicorrente(form.tipologia)
                          ? 'Obbligatorio per tipologie LICENZA / CANONE / CONSUMI / COMMISSIONI.'
                          : 'Opzionale.'}
                      </p>
                    </div>
                  </div>

                  {isRicorrente(form.tipologia) && (
                    <>
                      <h4 className="articolo-section-title articolo-section-subtitle">Default ricorrenza</h4>

                      <div className="bop-form-row">
                        <div className="bop-form-group">
                          <label className="bop-label">Sottotipo</label>
                          <input
                            type="text"
                            className="bop-input"
                            value={TIPOLOGIE_LABEL[form.tipologia] || form.tipologia}
                            readOnly
                            disabled
                          />
                          <p className="bop-field__hint">Coincide automaticamente con la tipologia.</p>
                        </div>

                        <div className="bop-form-group">
                          <label className="bop-label">Periodicità di fatturazione</label>
                          <select
                            className="bop-select"
                            value={form.periodicitaDefault}
                            onChange={(e) => setForm({ ...form, periodicitaDefault: e.target.value })}
                            disabled={!editTipologia}
                          >
                            <option value="">—</option>
                            {PERIODICITA_OPTIONS.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                          <p className="bop-field__hint">Ogni quanto matura ed è emesso il documento ricorrente (mensile, trimestrale, semestrale, annuale).</p>
                        </div>

                        <div className="bop-form-group">
                          <label className="bop-label">Durata totale del contratto (mesi)</label>
                          <input
                            type="number"
                            className="bop-input"
                            value={form.durataMesiDefault}
                            onChange={(e) => setForm({ ...form, durataMesiDefault: e.target.value })}
                            min="1"
                            step="1"
                            disabled={!editTipologia}
                          />
                          <p className="bop-field__hint">Lunghezza complessiva del contratto. Lascia vuoto per durata indeterminata (perpetua).</p>
                        </div>

                        <div className="bop-form-group">
                          <label className="bop-label">Modalità di determinazione importo</label>
                          <select
                            className="bop-select"
                            value={form.modalitaPagamentoDefault}
                            onChange={(e) => setForm({ ...form, modalitaPagamentoDefault: e.target.value })}
                            disabled={!editTipologia}
                          >
                            <option value="">—</option>
                            {MODALITA_PAGAMENTO_OPTIONS.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                          <p className="bop-field__hint">Come si determina l'importo: a preventivo (forfait anticipato), a consuntivo su volumi, a consuntivo su transazioni. Non è la condizione di pagamento della fattura (30/60 gg, RiBa…), che è un campo separato.</p>
                        </div>
                      </div>

                      {/* Tariffe condizionali */}
                      {form.modalitaPagamentoDefault === 'A_CONSUNTIVO_VOLUMI' &&
                       ['CONSUMI', 'COMMISSIONI'].includes(form.tipologia) && (
                        <div className="bop-form-row">
                          <div className="bop-form-group">
                            <label className="bop-label">Tariffa unitaria</label>
                            <input
                              type="number"
                              className="bop-input"
                              value={form.tariffaUnitariaDefault}
                              onChange={(e) => setForm({ ...form, tariffaUnitariaDefault: e.target.value })}
                              step="0.00001"
                              min="0"
                              disabled={!editTipologia}
                            />
                            <p className="bop-field__hint">es. €/sms, €/dispositivo, €/transazione</p>
                          </div>
                        </div>
                      )}

                      {form.modalitaPagamentoDefault === 'A_CONSUNTIVO_TRANSAZIONI' &&
                       form.tipologia === 'COMMISSIONI' && (
                        <div className="bop-form-row">
                          <div className="bop-form-group">
                            <label className="bop-label">Tariffa fissa per transazione</label>
                            <input
                              type="number"
                              className="bop-input"
                              value={form.tariffaFissaTransazioneDefault}
                              onChange={(e) => setForm({ ...form, tariffaFissaTransazioneDefault: e.target.value })}
                              step="0.00001"
                              min="0"
                              disabled={!editTipologia}
                            />
                            <p className="bop-field__hint">es. 0,03 €/transazione</p>
                          </div>

                          <div className="bop-form-group">
                            <label className="bop-label">Percentuale sul transato (%)</label>
                            <input
                              type="number"
                              className="bop-input"
                              value={form.percentualeTransazioneDefault}
                              onChange={(e) => setForm({ ...form, percentualeTransazioneDefault: e.target.value })}
                              step="0.0001"
                              min="0"
                              disabled={!editTipologia}
                            />
                            <p className="bop-field__hint">es. 1,2000 %</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <ApiInspector docs={API_DOCS} />
              </div>
            )}

            {/* TAB STORICO */}
            {/* TAB CODICI CLIENTE */}
            {activeTab === 'codici-cliente' && (
              <div className="articolo-codici-cliente">
                <div className="bop-card">
                  <div className="articolo-section-head">
                    <h3 className="articolo-section-title">Codici cliente</h3>
                    <div className="articolo-section-head__actions">
                      <button className="bop-btn bop-btn-primary" onClick={() => apriCodiceModal(null)}>
                        Aggiungi codice
                      </button>
                    </div>
                  </div>
                  <p className="bop-text-muted">
                    Il codice con cui ogni cliente identifica questo articolo nei propri ordini.
                    Serve all'import ordini per agganciare le righe all'articolo corretto.
                  </p>

                  {codiciLoading && <div className="bop-empty-state">Caricamento...</div>}

                  {!codiciLoading && codiciError && (
                    <div className="bop-empty-state">{codiciError}</div>
                  )}

                  {!codiciLoading && !codiciError && codiciCliente.length === 0 && (
                    <div className="bop-empty-state">Nessun codice cliente associato a questo articolo</div>
                  )}

                  {!codiciLoading && !codiciError && codiciCliente.length > 0 && (
                    <table className="bop-table">
                      <thead>
                        <tr>
                          <th>Cliente</th>
                          <th>Partita IVA</th>
                          <th>Codice del cliente</th>
                          <th className="bop-text-right">Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {codiciCliente.map(r => (
                          <tr key={r.id}>
                            <td>
                              {r.cliente.ragioneSociale
                                || `${r.cliente.nome || ''} ${r.cliente.cognome || ''}`.trim()
                                || '—'}
                            </td>
                            <td>{r.cliente.partitaIva || '—'}</td>
                            <td><strong>{r.codiceArticoloCliente}</strong></td>
                            <td className="bop-text-right">
                              <BopRowActions
                                actions={[
                                  { icon: <RowIcon.Edit />,  label: 'Modifica', onClick: () => apriCodiceModal(r), variant: 'primary' },
                                  { icon: <RowIcon.Trash />, label: 'Elimina',  onClick: () => handleEliminaCodice(r), variant: 'danger' },
                                ]}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'storico' && (
              <div className="articolo-storico">
                <div className="bop-card">
                  <h3 className="articolo-section-title">Storico attività</h3>

                  {logLoading && <div className="bop-empty-state">Caricamento...</div>}

                  {!logLoading && logError && (
                    <div className="bop-empty-state">Storico non disponibile</div>
                  )}

                  {!logLoading && !logError && logList.length === 0 && (
                    <div className="bop-empty-state">Nessuna attività registrata</div>
                  )}

                  {!logLoading && !logError && logList.length > 0 && (
                    <table className="bop-table">
                      <thead>
                        <tr>
                          <th>Data / Ora</th>
                          <th>Utente</th>
                          <th>Azione</th>
                          <th>Dettaglio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logList.map(r => {
                          const badgeClass =
                            r.azione === 'Creazione'      ? 'bop-badge-success' :
                            r.azione === 'Riattivazione'  ? 'bop-badge-success' :
                            r.azione === 'Disattivazione' ? 'bop-badge-warning' :
                                                            'bop-badge-info';
                          return (
                            <tr key={r.id}>
                              <td>{new Date(r.createdAt).toLocaleString('it-IT')}</td>
                              <td>{r.utenteName}</td>
                              <td><span className={`bop-badge ${badgeClass}`}>{r.azione}</span></td>
                              <td>{r.dettaglio || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                <ApiInspector docs={API_DOCS} />
              </div>
            )}
          </div>
        </div>

        {/* Modale codice cliente */}
        {codiceModalOpen && (
          <div className="bop-modal-overlay">
            <div className="bop-modal">
              <div className="bop-modal__header">
                <h2 className="bop-modal__title">
                  {codiceInModifica ? 'Modifica codice cliente' : 'Nuovo codice cliente'}
                </h2>
                <button
                  className="bop-modal__close"
                  onClick={chiudiCodiceModal}
                  aria-label="Chiudi"
                  disabled={codiceSaving}
                >
                  ✕
                </button>
              </div>
              <div className="bop-modal__body">
                <div className="bop-form-group">
                  <label className="bop-label">Cliente *</label>
                  {codiceInModifica ? (
                    <input
                      type="text"
                      className="bop-input"
                      value={
                        codiceInModifica.cliente.ragioneSociale
                        || `${codiceInModifica.cliente.nome || ''} ${codiceInModifica.cliente.cognome || ''}`.trim()
                      }
                      disabled
                    />
                  ) : (
                    <NominativoClientePicker
                      societaId={articolo.societaId}
                      value={codiceClienteId}
                      onChange={(nominativoId) => {
                        setCodiceClienteId(nominativoId);
                        if (codiceModalError) setCodiceModalError('');
                      }}
                      placeholder="Cerca il cliente per ragione sociale..."
                    />
                  )}
                </div>

                <div className="bop-form-group">
                  <label className="bop-label" htmlFor="codice-cliente-valore">
                    Codice usato dal cliente *
                  </label>
                  <input
                    id="codice-cliente-valore"
                    type="text"
                    className="bop-input"
                    value={codiceValore}
                    onChange={e => {
                      setCodiceValore(e.target.value);
                      if (codiceModalError) setCodiceModalError('');
                    }}
                    onKeyDown={e => { if (e.key === 'Enter' && !codiceSaving) handleSalvaCodice(); }}
                    placeholder="es. ART-4471/B"
                    disabled={codiceSaving}
                    autoFocus={!!codiceInModifica}
                  />
                </div>

                {codiceModalError && (
                  <p className="bop-text-danger">{codiceModalError}</p>
                )}
              </div>
              <div className="bop-modal__footer">
                <button className="bop-btn bop-btn-secondary" onClick={chiudiCodiceModal} disabled={codiceSaving}>
                  Annulla
                </button>
                <button className="bop-btn bop-btn-primary" onClick={handleSalvaCodice} disabled={codiceSaving}>
                  {codiceSaving ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modale duplicazione articolo */}
        {duplicaOpen && (
          <div className="bop-modal-overlay">
            <div className="bop-modal">
              <div className="bop-modal__header">
                <h2 className="bop-modal__title">Duplica articolo</h2>
                <button
                  className="bop-modal__close"
                  onClick={chiudiDuplicaModal}
                  aria-label="Chiudi"
                  disabled={duplicating}
                >
                  ✕
                </button>
              </div>
              <div className="bop-modal__body">
                <p className="bop-text-muted articolo-duplica-modal__intro">
                  Stai per creare una copia dell'articolo <strong>{articolo.codice}</strong> —{' '}
                  <em>{articolo.descrizioneBase}</em>. Tutti i campi verranno copiati nel nuovo articolo;
                  inserisci il codice da assegnare alla copia.
                </p>
                <div className="bop-form-group">
                  <label className="bop-label" htmlFor="duplica-codice">Codice nuovo articolo *</label>
                  <input
                    id="duplica-codice"
                    type="text"
                    className="bop-input"
                    value={duplicaCodice}
                    onChange={e => {
                      setDuplicaCodice(e.target.value);
                      if (duplicaError) setDuplicaError('');
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !duplicating) {
                        e.preventDefault();
                        handleDuplica();
                      }
                    }}
                    disabled={duplicating}
                    autoFocus
                  />
                  <p className="bop-field__hint">
                    Il codice verrà convertito in MAIUSCOLO e dovrà essere diverso da quello attuale.
                  </p>
                </div>
                {duplicaError && (
                  <p className="bop-text-danger articolo-duplica-modal__error">{duplicaError}</p>
                )}
              </div>
              <div className="bop-modal__footer">
                <button
                  className="bop-btn bop-btn-secondary"
                  onClick={chiudiDuplicaModal}
                  disabled={duplicating}
                >
                  Annulla
                </button>
                <button
                  className="bop-btn bop-btn-primary"
                  onClick={handleDuplica}
                  disabled={duplicating || !duplicaCodice.trim()}
                >
                  {duplicating ? 'Duplicazione...' : 'Duplica'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modale eliminazione articolo */}
        {eliminaOpen && (
          <div className="bop-modal-overlay">
            <div className="bop-modal">
              <div className="bop-modal__header">
                <h2 className="bop-modal__title">Elimina articolo</h2>
                <button
                  className="bop-modal__close"
                  onClick={chiudiEliminaModal}
                  aria-label="Chiudi"
                  disabled={eliminating}
                >
                  ✕
                </button>
              </div>
              <div className="bop-modal__body">
                {!eliminaUsatoIn ? (
                  <p className="bop-text-muted">
                    Stai per eliminare definitivamente l'articolo{' '}
                    <strong>{articolo.codice}</strong> — <em>{articolo.descrizioneBase}</em>.
                    L'eliminazione è possibile solo se l'articolo non è mai stato utilizzato.
                    L'azione non è reversibile.
                  </p>
                ) : (
                  <>
                    <p className="bop-text-danger">
                      L'articolo non può essere eliminato perché è già utilizzato.
                      In alternativa puoi <strong>disattivarlo</strong> per nasconderlo dalle liste.
                    </p>
                    <p className="bop-text-muted">Riferimenti trovati:</p>
                    <ul className="articolo-elimina-usato-list">
                      {Object.entries(eliminaUsatoIn).map(([k, n]) => (
                        <li key={k}>
                          <strong>{ETICHETTE_USATO_IN[k] || k}</strong>: {n}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {eliminaError && !eliminaUsatoIn && (
                  <p className="bop-text-danger articolo-duplica-modal__error">{eliminaError}</p>
                )}
              </div>
              <div className="bop-modal__footer">
                <button
                  className="bop-btn bop-btn-secondary"
                  onClick={chiudiEliminaModal}
                  disabled={eliminating}
                >
                  {eliminaUsatoIn ? 'Chiudi' : 'Annulla'}
                </button>
                {!eliminaUsatoIn && (
                  <button
                    className="bop-btn bop-btn-danger"
                    onClick={handleElimina}
                    disabled={eliminating}
                  >
                    {eliminating ? 'Eliminazione...' : 'Elimina definitivamente'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
